/**
 * The format standards as closed selects (standards selects spec, ratified 2026-10-01):
 * currency, calendar year, fiscal year and language variant. Each is a fixed list of
 * presets and nothing else (D-4: no free text, so a later deterministic check can read
 * them). "Not set" is the field's absence.
 *
 * Ids never change once shipped; labels and examples may. A new currency is one edit to
 * `CURRENCIES` (type, list, schema), then `npm run contract:sync`.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";

/** The 17 currencies (D-6), ISO 4217 codes. */
export type CurrencyCode =
  | "USD"
  | "EUR"
  | "GBP"
  | "AUD"
  | "CAD"
  | "NZD"
  | "CHF"
  | "JPY"
  | "CNY"
  | "HKD"
  | "SGD"
  | "INR"
  | "SEK"
  | "NOK"
  | "DKK"
  | "ZAR"
  | "AED";

/** How an amount is written (spec §3.1). */
export type CurrencyStyle = "symbol-short" | "symbol-full" | "code-short" | "symbol-word" | "code-word";

/** The currency standard: both parts are required (spec §2). */
export type CurrencyFormat = { currency: CurrencyCode; style: CurrencyStyle };

export type CalendarYearFormat = "mon-yyyy" | "mon-yy" | "month-yyyy" | "mm-yyyy" | "yyyy-mm" | "q-yyyy";

export type FiscalStyle = "fy-yy" | "fy-yyyy" | "fy-span-short" | "fy-span-long" | "fy-word";

/** 1 = January ... 12 = December. */
export type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

/** The fiscal year standard; the year end is optional (spec §3.3). */
export type FiscalYearFormat = { style: FiscalStyle; endMonth?: MonthNumber };

export type LanguageVariant = "en-GB" | "en-US" | "en-AU" | "en-CA";

/** A currency's display data. `symbol` is the prefix the symbol styles write, one per
 * currency so a later check can tell them apart (the three krone/krona write their ISO
 * code, yuan writes CN¥); `noun` is the plural the prompt line uses. */
export type CurrencyInfo = { code: CurrencyCode; name: string; noun: string; symbol: string };

export const CURRENCIES: readonly CurrencyInfo[] = [
  { code: "USD", name: "US dollar", noun: "US dollars", symbol: "$" },
  { code: "EUR", name: "Euro", noun: "euros", symbol: "€" },
  { code: "GBP", name: "British pound", noun: "British pounds", symbol: "£" },
  { code: "AUD", name: "Australian dollar", noun: "Australian dollars", symbol: "A$" },
  { code: "CAD", name: "Canadian dollar", noun: "Canadian dollars", symbol: "C$" },
  { code: "NZD", name: "New Zealand dollar", noun: "New Zealand dollars", symbol: "NZ$" },
  { code: "CHF", name: "Swiss franc", noun: "Swiss francs", symbol: "CHF" },
  { code: "JPY", name: "Japanese yen", noun: "Japanese yen", symbol: "¥" },
  { code: "CNY", name: "Chinese yuan", noun: "Chinese yuan", symbol: "CN¥" },
  { code: "HKD", name: "Hong Kong dollar", noun: "Hong Kong dollars", symbol: "HK$" },
  { code: "SGD", name: "Singapore dollar", noun: "Singapore dollars", symbol: "S$" },
  { code: "INR", name: "Indian rupee", noun: "Indian rupees", symbol: "₹" },
  { code: "SEK", name: "Swedish krona", noun: "Swedish kronor", symbol: "SEK" },
  { code: "NOK", name: "Norwegian krone", noun: "Norwegian kroner", symbol: "NOK" },
  { code: "DKK", name: "Danish krone", noun: "Danish kroner", symbol: "DKK" },
  { code: "ZAR", name: "South African rand", noun: "South African rand", symbol: "R" },
  { code: "AED", name: "UAE dirham", noun: "UAE dirhams", symbol: "AED" },
];

/** `rule` is what a later check can test (spec §3.1). */
type CurrencyStyleInfo = { id: CurrencyStyle; label: string; rule: string };

export const CURRENCY_STYLES: readonly CurrencyStyleInfo[] = [
  { id: "symbol-short", label: "Symbol, short scale", rule: "symbol, at most one decimal, K/M/B suffix" },
  { id: "symbol-full", label: "Symbol, full number", rule: "symbol, full number, thousands separators" },
  { id: "code-short", label: "Code, short scale", rule: "ISO code, space, K/M/B suffix" },
  { id: "symbol-word", label: "Symbol, scale as a word", rule: "symbol, thousand/million/billion spelled out" },
  { id: "code-word", label: "Code, scale as a word", rule: "ISO code, scale spelled out" },
];

type PresetInfo<Id extends string> = { id: Id; label: string; example: string };

export const CALENDAR_YEAR_FORMATS: readonly PresetInfo<CalendarYearFormat>[] = [
  { id: "mon-yyyy", label: "Short month and year", example: "Mar 2025" },
  // A8 (Dan 2026-10-02). A stored "mon-yy" reads as not set on a bundle from before it.
  { id: "mon-yy", label: "Short month and two-digit year", example: "Mar 25" },
  { id: "month-yyyy", label: "Full month and year", example: "March 2025" },
  { id: "mm-yyyy", label: "Numeric month and year", example: "03/2025" },
  { id: "yyyy-mm", label: "ISO year and month", example: "2025-03" },
  { id: "q-yyyy", label: "Quarter and year", example: "Q1 2025" },
];

export const FISCAL_STYLES: readonly PresetInfo<FiscalStyle>[] = [
  { id: "fy-yy", label: "FY and two digits", example: "FY25" },
  { id: "fy-yyyy", label: "FY and four digits", example: "FY2025" },
  { id: "fy-span-short", label: "FY and year span", example: "FY24/25" },
  { id: "fy-span-long", label: "FY and long year span", example: "FY 2024/25" },
  { id: "fy-word", label: "Year then \"financial year\"", example: "2025 financial year" },
];

export const MONTH_NAMES: readonly string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const LANGUAGE_VARIANTS: ReadonlyArray<{ id: LanguageVariant; label: string }> = [
  { id: "en-GB", label: "British English" },
  { id: "en-US", label: "American English" },
  { id: "en-AU", label: "Australian English" },
  { id: "en-CA", label: "Canadian English" },
];

export const currencyFormatSchema = z.object({
  currency: z.enum(["USD", "EUR", "GBP", "AUD", "CAD", "NZD", "CHF", "JPY", "CNY", "HKD", "SGD", "INR", "SEK", "NOK", "DKK", "ZAR", "AED"]),
  style: z.enum(["symbol-short", "symbol-full", "code-short", "symbol-word", "code-word"]),
});

export const calendarYearFormatSchema = z.enum(["mon-yyyy", "mon-yy", "month-yyyy", "mm-yyyy", "yyyy-mm", "q-yyyy"]);

export const fiscalYearFormatSchema = z.object({
  style: z.enum(["fy-yy", "fy-yyyy", "fy-span-short", "fy-span-long", "fy-word"]),
  endMonth: z
    .union([
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
      z.literal(6),
      z.literal(7),
      z.literal(8),
      z.literal(9),
      z.literal(10),
      z.literal(11),
      z.literal(12),
    ])
    .optional(),
});

export const languageVariantSchema = z.enum(["en-GB", "en-US", "en-AU", "en-CA"]);

expectParity<z.infer<typeof currencyFormatSchema>, CurrencyFormat>(true);
expectParity<z.infer<typeof calendarYearFormatSchema>, CalendarYearFormat>(true);
expectParity<z.infer<typeof fiscalYearFormatSchema>, FiscalYearFormat>(true);
expectParity<z.infer<typeof languageVariantSchema>, LanguageVariant>(true);
